/** Gaist (W5): Typen und Antwort als Strom (Server-Sent Events ueber fetch, weil die Frage
 *  per POST mit CSRF-Kopf geht; EventSource kann nur GET). */
import { ApiFehler, holeCsrf, meldung } from "./client";
import type { GaistVorschlag } from "./typen";

export type Quelle = { id: string; titel: string };

/** Erste Frage, wenn der Chat aus einem Brief heraus beginnt („Brief erklaeren“). */
export const ERKLAEREN = "Erkläre mir diesen Brief: Worum geht es, was wird von mir verlangt, und welche Fristen laufen?";

/** Beispielfragen fuer einen leeren Chat. */
export const VORSCHLAEGE = [
  "Welche Fristen laufen in den nächsten vier Wochen ab?",
  "Was habe ich dieses Jahr für Versicherungen bezahlt?",
  "Erkläre mir meinen letzten Brief.",
];
/** W69 (D-230): fester KI-Satz in Leerzustand, Fußtext und Chat; nie Teil einer Antwort. */
export const KI_SATZ = "Gaist ist eine KI und kann Fehler machen.";
/** W82 Web-5h2 (HW19, D-645): Blatt „Mit Gaist entwerfen“, erst mit dem Vorschlag (Satzquelle für Web, iOS und Android). */
export const UEBERNEHMEN_SATZ = "Ersetzt Betreff und Text erst mit „Übernehmen“.";
/** W73 (D-272): leerer Chat ohne Kennung (`/assistent/neu`); angelegt wird er erst beim ersten Senden. */
export const NEUER_CHAT = "neu";
export const NEUER_CHAT_WEG = `/assistent/${NEUER_CHAT}`;
/** Steht die Adresse fuer einen noch nicht angelegten Chat? Kennungen des Servers sind UUIDs, nie „neu“. */
export function neuerChat(id: string | undefined): boolean {
  return id === NEUER_CHAT;
}
/** W15: Brief-Entwurf, den der Gaist auf Bitte unter „Briefe“ angelegt hat. */
export type BriefEntwurf = { id: string; betreff: string; vorlage: string };
export type Nachricht = {
  id: string;
  rolle: "nutzer" | "assistent";
  text: string;
  quellen: Quelle[];
  briefe?: BriefEntwurf[];
  erstellt: string;
  /** W80: Vorschlaege dieser Antwort als Karten; fehlt bei aelteren Servern */
  vorschlaege?: GaistVorschlag[];
  /** W82 Web-8 W8-4 (S93, WN-109): der Gaist hat die Antwort abgelehnt; `text` ist die Ablehnung, Quellen und Vorschlaege der
   *  verworfenen Antwort fehlen. Die Antwort ist gespeichert, die naechste Frage geht wie sonst. Fehlt bei normalen Antworten. */
  abgelehnt?: boolean;
};
/** W80 (plan-w80.md 2.5): mit diesem Kopf bietet der Server dem Gaist Vorschlaege als Karte an; ohne ihn wie bisher. */
export const KARTEN_KOPF = "X-Gaist-Karten";
export type Chat = { id: string; titel: string; dokument_id: string | null; erstellt: string; geaendert: string };
/** antwort_laeuft: der Server schreibt gerade eine Antwort in diesen Chat (auch nach Verlassen der Seite). */
export type ChatDetail = Chat & { nachrichten: Nachricht[]; antwort_laeuft?: boolean };

export type Ereignis =
  | { art: "frage"; nachricht: Nachricht }
  | { art: "status"; werkzeug: string; text: string }
  | { art: "quelle"; quelle: Quelle }
  | { art: "text"; t: string }
  | { art: "zwischen" }
  | { art: "fertig"; nachricht: Nachricht }
  | { art: "fehler"; text: string };

function ereignis(art: string, daten: Record<string, unknown>): Ereignis | null {
  switch (art) {
    case "frage":
      return { art, nachricht: daten as Nachricht };
    case "status":
      return { art, werkzeug: String(daten.werkzeug), text: String(daten.text) };
    case "quelle":
      return { art, quelle: { id: String(daten.id), titel: String(daten.titel) } };
    case "text":
      return { art, t: String(daten.t) };
    case "zwischen":
      return { art };
    case "fertig": {
      const nachricht: Nachricht = { ...(daten as Omit<Nachricht, "rolle">), rolle: "assistent" };
      // S93 (WN-109): eine Ablehnung traegt nie Quellen oder Karten der verworfenen Antwort, auch wenn der Strom sie schon nannte
      return { art, nachricht: nachricht.abgelehnt === true ? { ...nachricht, quellen: [], vorschlaege: [] } : nachricht };
    }
    case "fehler":
      return { art, text: String(daten.text) };
    default:
      return null; // unbekannte Ereignisse spaeterer Versionen ueberspringen
  }
}

/** Zerlegt einen SSE-Puffer in fertige Bloecke; der unvollstaendige Rest bleibt im Puffer. */
export function sseBloecke(puffer: string): { bloecke: Ereignis[]; rest: string } {
  const teile = puffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = teile.pop() ?? "";
  const bloecke: Ereignis[] = [];
  for (const teil of teile) {
    let art = "message";
    const daten: string[] = [];
    for (const zeile of teil.split("\n")) {
      if (zeile.startsWith("event:")) art = zeile.slice(6).trim();
      else if (zeile.startsWith("data:")) daten.push(zeile.slice(5).trimStart());
    }
    if (!daten.length) continue;
    try {
      const e = ereignis(art, JSON.parse(daten.join("\n")) as Record<string, unknown>);
      if (e) bloecke.push(e);
    } catch {
      // kaputter Block: ueberspringen, der Rest des Stroms bleibt nutzbar
    }
  }
  return { bloecke, rest };
}

/** Laufende Antworten (W22d-w4): die Navigation zeigt den Funken des Briefgeists am Gaist gelb,
 *  solange eine Antwort entsteht, wie der Tab der iOS-App (W22d-8). Zaehlt jeden Aufruf von `fragen`
 *  vom Absenden bis zum Ende des Stroms, auch bei Fehler oder Abbruch. */
let laufend = 0;
const beobachter = new Set<() => void>();

export function antwortLaeuft(): boolean {
  return laufend > 0;
}

export function antwortBeobachten(melden: () => void): () => void {
  beobachter.add(melden);
  return () => {
    beobachter.delete(melden);
  };
}

function laufendAendern(um: number): void {
  laufend += um;
  beobachter.forEach((melden) => melden());
}

export async function fragen(
  chatId: string,
  text: string,
  beiEreignis: (e: Ereignis) => void,
  signal?: AbortSignal,
): Promise<void> {
  laufendAendern(1);
  try {
    await stromLesen(chatId, text, beiEreignis, signal);
  } finally {
    laufendAendern(-1);
  }
}

async function stromLesen(chatId: string, text: string, beiEreignis: (e: Ereignis) => void, signal?: AbortSignal): Promise<void> {
  const antwort = await fetch(`/api/assistent/chats/${chatId}/nachrichten`, {
    method: "POST",
    headers: { Accept: "text/event-stream", "Content-Type": "application/json", "X-CSRF-Token": holeCsrf(), [KARTEN_KOPF]: "1" },
    body: JSON.stringify({ text }),
    credentials: "same-origin",
    signal,
  });
  if (!antwort.ok || !antwort.body) {
    const daten: unknown = await antwort.json().catch(() => null);
    throw new ApiFehler(antwort.status, meldung(antwort.status, daten), daten);
  }
  const leser = antwort.body.pipeThrough(new TextDecoderStream()).getReader();
  let puffer = "";
  for (;;) {
    const { value, done } = await leser.read();
    if (done) break;
    const { bloecke, rest } = sseBloecke(puffer + value);
    puffer = rest;
    bloecke.forEach(beiEreignis);
  }
  sseBloecke(puffer + "\n\n").bloecke.forEach(beiEreignis);
}

/** Antworttext in Teile zerlegen: Absaetze, Listen, Entwuerfe, Quellenmarken. Kein HTML: die
 *  Oberflaeche baut daraus React-Elemente (Antworttext.tsx). */
export type Inline = { art: "text"; text: string } | { art: "fett"; text: string } | { art: "quelle"; id: string };
export type Block =
  | { art: "absatz"; inhalt: Inline[] }
  | { art: "liste"; nummeriert: boolean; punkte: Inline[][] }
  | { art: "entwurf"; text: string }
  | { art: "code"; text: string };

const MARKE = /\[\[\s*dok:\s*([0-9a-fA-F-]{36})\s*\]\]|\*\*([^*]+)\*\*/gi;
// Reste, die keine gueltige Marke sind (unfertig, falsche Klammern): nie roh zeigen, auch nicht
// waehrend des Stroms (der Server entfernt sie beim Speichern, Verifier W5 Runde 3)
const MARKE_REST = /[ \t]?\[\[\s*dok:[^\s[\]]*\]{0,2}/gi;

/** Text ohne jede Quellenmarke (Entwurf, Code, Fettschrift: dort gibt es keine Fussnote). */
function ohneMarken(t: string): string {
  return t.replace(/[ \t]?\[\[\s*dok:[^\]\n]*\]\]/gi, "").replace(MARKE_REST, "");
}

export function inline(text: string): Inline[] {
  const aus: Inline[] = [];
  let letzte = 0;
  const textTeil = (t: string) => {
    const rein = t.replace(MARKE_REST, "");
    if (rein) aus.push({ art: "text", text: rein });
  };
  for (const m of text.matchAll(MARKE)) {
    if (m.index > letzte) textTeil(text.slice(letzte, m.index));
    if (m[1]) aus.push({ art: "quelle", id: m[1].toLowerCase() });
    else if (m[2] && ohneMarken(m[2])) aus.push({ art: "fett", text: ohneMarken(m[2]) });
    letzte = m.index + m[0].length;
  }
  if (letzte < text.length) textTeil(text.slice(letzte));
  return aus;
}

export function bloecke(text: string, streamt = false): Block[] {
  // Waehrend des Streamings: eine halb angekommene Quellenmarke nicht als Text zeigen; W82 Web-5h-N2 a1: ebenso ein offenes `**`
  // am Ende (die fette Wendung erscheint erst mit ihrem Gegenstueck, als Ganzes)
  const ohneMarke = streamt ? text.replace(/\[\[[^\]]*\]?$/, "") : text;
  const sauber = streamt && (ohneMarke.match(/\*\*/g)?.length ?? 0) % 2 === 1 ? ohneMarke.replace(/\*\*[^*]*$/, "") : ohneMarke;
  const aus: Block[] = [];
  const zeilen = sauber.split("\n");
  let absatz: string[] = [];
  let liste: { nummeriert: boolean; punkte: Inline[][] } | null = null;
  const absatzEnde = () => {
    if (absatz.length) aus.push({ art: "absatz", inhalt: inline(absatz.join("\n")) });
    absatz = [];
  };
  const listeEnde = () => {
    if (liste) aus.push({ art: "liste", ...liste });
    liste = null;
  };
  for (let i = 0; i < zeilen.length; i++) {
    const zeile = zeilen[i] ?? "";
    const zaun = /^```\s*(\w*)\s*$/.exec(zeile);
    if (zaun) {
      absatzEnde();
      listeEnde();
      const inhalt: string[] = [];
      i++;
      while (i < zeilen.length && !/^```\s*$/.test(zeilen[i] ?? "")) inhalt.push(zeilen[i++] ?? "");
      // Quellenmarken gehoeren nicht in einen Entwurf, der kopiert wird (Verifier W5, F6)
      const text = ohneMarken(inhalt.join("\n"));
      aus.push({ art: zaun[1] === "entwurf" ? "entwurf" : "code", text });
      continue;
    }
    const punkt = /^\s*(?:[-*•]|(\d+)[.)])\s+(.*)$/.exec(zeile);
    if (punkt) {
      absatzEnde();
      const nummeriert = Boolean(punkt[1]);
      if (liste && liste.nummeriert !== nummeriert) listeEnde();
      liste ??= { nummeriert, punkte: [] };
      liste.punkte.push(inline(punkt[2] ?? ""));
      continue;
    }
    if (!zeile.trim()) {
      absatzEnde();
      listeEnde();
      continue;
    }
    listeEnde();
    absatz.push(zeile.replace(/^#{1,6}\s+/, ""));
  }
  absatzEnde();
  listeEnde();
  return aus;
}

/** Steht als Letztes eine Frage ohne Antwort da? „wartet“, solange der Server noch an der Antwort
 *  schreibt (seine Angabe, keine Uhr: nach einem KI-Fehler ist die Frage sofort „ohne“; Verifier W5
 *  Runde 2, N2). */
export function offeneFrage(chat: ChatDetail | undefined): "wartet" | "ohne" | null {
  const letzte = chat?.nachrichten.at(-1);
  if (!letzte || letzte.rolle !== "nutzer") return null;
  return chat?.antwort_laeuft ? "wartet" : "ohne";
}
